import React, {useEffect, useState, useRef} from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Divider,
  TableContainer,
  ModalFooter,
  TableCaption,
  Tfoot,
  useDisclosure,
  Checkbox,
  Flex,
  Text,
  Button,
} from '@chakra-ui/react';
import {useFieldArray, useForm} from 'react-hook-form';
import {CustomButton, CustomCheckbox, CustomInput, SubmitButton} from '../../General/FormInputs';
import BoxCard from '../../General/BoxCard';
import {FaTrashAlt, FaSave, FaPlusSquare} from 'react-icons/fa';
import {useClient} from '../../../views/Maintenance/ClientMaintenance';
import {setGrid} from '../../../helpers/SetGrid';
import {get, post, postDelete} from '../../../service/ApiService';
import {Arranger, Client} from '../../../service/Urls';
import {isAdmin} from '../../../helpers/localStorage';
import ErrorHelper from '../../../service/ErrorHelper';
import {formatDate} from '../../../helpers/Functions';
import {useQueryClient, useMutation} from 'react-query';
import {ArrangerSelect} from '../../Arranger/ArrangerSelect';

export default function Arrangers() {
  const {client} = useClient();
  const [selectedArrangerIndex, setSelectedArrangerIndex] = useState(0);
  const [toastResponse, setToastResponse] = useState(undefined);
  const [isSalesMan, setIsSalesMan] = useState(true);

  const queryClient = useQueryClient();

  const {isOpen, onOpen, onClose} = useDisclosure();

  useEffect(() => {
    if (isAdmin()) setIsSalesMan(false);
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    watch,
    formState: {errors},
  } = useForm();

  const {
    register: registerCombine,
    handleSubmit: handleSubmitCombine,
    setValue: setValueCombine,
    formState: {errors: errorsCombine},
    reset: resetCombine,
  } = useForm();

  const {
    fields: arrangers,
    prepend: prependArranger,
    remove: removeArranger,
    update: updateArranger,
  } = useFieldArray({
    name: 'arrangers',
    control,
  });

  const {mutate: combineArrangers} = useMutation({
    mutationFn: async data => {
      return post(Arranger.COMBINE + data.arrIdCombine1 + '/' + data.arrIdCombine2, {});
    },
    onSuccess: data => {
      queryClient.invalidateQueries('client');
      setToastResponse({response: {isSuccess: true}, successMessage: 'Successfully combined arrangers'});
      resetCombine({arrIdCombine1: '', arrIdCombine2: ''});
    },
    onError: error => {
      setToastResponse({response: {isSuccess: false, error: error}, errorMessage: error});
    },
  });

  useEffect(() => {
    const arrangers = client?.arrangers?.sort((a, b) => b.lastUpdated?.localeCompare(a.lastUpdated) ?? -1) ?? [];
    const parsedArrangers = arrangers.map(arranger => {
      return {
        ...arranger,
        clientId: client?.id,
      };
    });

    reset({
      arrangers: parsedArrangers,
    });
  }, [client]);

  const addArranger = async () => {
    const name = prompt("What is the Arranger's name?");

    if (name == null) return;

    const ar = {
      aliases: [],
      arInact: false,
      arName: name,
      clientId: client?.id,
      arClient: client?.clId,
    };

    const response = await post(Arranger.ADD, ar);

    if (!response.isSuccess) {
      setToastResponse({response: response, errorMessage: response.error});
      return;
    }

    ar.id = response.id;
    ar.arId = response.arId;
    const updateResponse = await post(Arranger.UPDATE + '/' + ar.id, ar);

    if (updateResponse.isSuccess) {
      queryClient.invalidateQueries('client');
      setToastResponse({response: response, successMessage: 'Successfully added ' + ar.arName});
    } else {
      setToastResponse({response: response, errorMessage: response.error});
    }
  };

  const deleteArranger = async (index: number) => {
    handleSubmit(async data => {
      const arranger = data.arrangers[index];
      if (confirm('Are you sure you want to remove ' + arranger?.arName + '?')) {
        const response = await postDelete(Arranger.DELETE + '/' + arranger?.id);

        if (!response.isSuccess) {
          setToastResponse({response: response, errorMessage: response.error});
          return;
        }

        queryClient.invalidateQueries('client');
        setToastResponse({response: response, successMessage: 'Successfully deleted ' + arranger?.arName});
      }
    })();
  };

  const openAliasModal = (index: number) => {
    setSelectedArrangerIndex(index);
    onOpen();
  };

  const addAlias = () => {
    const arranger = getValues('arrangers')[selectedArrangerIndex];
    const aliases = arranger?.aliases ?? [];
    const newAliases = [...aliases, {nseq: Math.max(...aliases.map(({nseq}) => nseq ?? 0), 0) + 1, calias: ''}];
    updateArranger(selectedArrangerIndex, {...arranger, aliases: newAliases});
  };

  const removeAlias = (index: number) => {
    const arranger = getValues('arrangers')[selectedArrangerIndex];
    const aliases = arranger?.aliases ?? [];
    const newAliases = aliases.filter((_, i) => i !== index);
    updateArranger(selectedArrangerIndex, {...arranger, aliases: newAliases});
  };

  const saveArranger = async (index: number) => {
    handleSubmit(async data => {
      const arranger = data.arrangers[index];
      const response = await post(Arranger.UPDATE + '/' + arranger?.id, arranger);

      if (!response.isSuccess) {
        setToastResponse({response: response, errorMessage: response.error});
        return;
      }

      queryClient.invalidateQueries('client');
      setToastResponse({response: response, successMessage: 'Successfully updated ' + arranger?.arName});
    })();
  };

  return (
    <Box mr="2">
      <BoxCard>
        <form onSubmit={handleSubmitCombine(combineArrangers)}>
          <Text textAlign="left" fontSize="lg" fontWeight="semibold" mb="5">
            Combine Arrangers
          </Text>
          <Flex alignItems="flex-end">
            <Box w="35%" mr="30px">
              <ArrangerSelect
                register={registerCombine}
                errors={errorsCombine}
                id="arrIdCombine1"
                required
                setRHFValue={setValueCombine}
                clientId={client?.client?.id}
              />
            </Box>
            <Box w="35%" mr="2">
              <ArrangerSelect
                register={registerCombine}
                errors={errorsCombine}
                id="arrIdCombine2"
                required
                setRHFValue={setValueCombine}
                clientId={client?.client?.id}
              />
            </Box>
            <Button type="submit" colorScheme="tertiary" title="Combine" size="md">
              Combine
            </Button>
          </Flex>
        </form>
      </BoxCard>
      <form onSubmit={handleSubmit(saveArranger)}>
        <BoxCard>
          <TableContainer>
            <Table id="arr" border="solid 2px #0c5f78" variant="striped" colorScheme="mint" size={'md'}>
              <TableCaption>Arrangers</TableCaption>
              <Thead>
                <Tr border="solid 2px #0c5f78">
                  <Th>Arranger Id</Th>
                  <Th>Arranger Name</Th>
                  <Th>Date Added</Th>
                  <Th>Last Updated</Th>
                  <Th>Inactive</Th>
                  <Th>Aliases</Th>

                  {!isSalesMan ? (
                    <Td>
                      <IconButton colorScheme="tertiary" title="Add" size="md" icon={<FaPlusSquare />} onClick={() => addArranger()} />
                    </Td>
                  ) : null}
                </Tr>
              </Thead>
              <Tbody>
                {arrangers.map((arranger, index) => {
                  const arInactId = `arrangers.${index}.arInact` as const;

                  const arrangerErrors = errors?.arrangers?.[index] ?? {};
                  return (
                    <Tr key={arranger.id}>
                      <Td>{arranger.arId}</Td>
                      <Td>{arranger.arName}</Td>
                      <Td>{formatDate(arranger.dateAdded)}</Td>
                      <Td>{formatDate(arranger.lastUpdated)}</Td>
                      <Td>
                        <CustomCheckbox id={arInactId} control={control} errors={arrangerErrors} />
                      </Td>
                      <Td>
                        <CustomButton name="Edit Aliases" onClick={() => openAliasModal(index)} />
                      </Td>
                      {!isSalesMan && (
                        <Td>
                          <IconButton
                            onClick={() => saveArranger(index)}
                            colorScheme="tertiary"
                            title="Save"
                            size="md"
                            icon={<FaSave />}
                            mr="2"
                            aria-label="Save"
                          />
                          <IconButton
                            onClick={() => deleteArranger(index)}
                            colorScheme="tertiary"
                            title="Delete"
                            size="md"
                            icon={<FaTrashAlt />}
                            aria-label="Delete"
                          />
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </Tbody>
              {/* <Tfoot>
                <Tr>{elements.footer}</Tr>
              </Tfoot> */}
            </Table>
          </TableContainer>
        </BoxCard>
      </form>
      <Modal blockScrollOnMount={false} size={'4xl'} isOpen={isOpen} onClose={onClose}>
        <form>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader id="header">Aliases</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Divider mt="5" colorScheme={'mint'} width={{base: '100%'}} />
              <TableContainer width={{base: '100%'}}>
                <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'}>
                  <Thead>
                    <Tr border="solid 2px #0c5f78">
                      <Th>Alias Number</Th>
                      <Th>Alias</Th>
                      <Th>
                        {!isSalesMan && (
                          <IconButton
                            colorScheme="tertiary"
                            title="Add"
                            size="md"
                            icon={<FaPlusSquare />}
                            onClick={addAlias}
                            aria-label="Add Alias"
                          />
                        )}
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {arrangers[selectedArrangerIndex]?.aliases.map((alias, index) => {
                      const calasId = `arrangers.${selectedArrangerIndex}.aliases.${index}.calias` as const;

                      return (
                        <Tr key={alias.id}>
                          <Td>{alias.nseq}</Td>
                          <Td>
                            <CustomInput id={calasId} register={register} name="Alias" />
                          </Td>
                          <Td>
                            {!isSalesMan && (
                              <IconButton
                                colorScheme="tertiary"
                                title="Delete"
                                size="md"
                                icon={<FaTrashAlt />}
                                onClick={() => removeAlias(index)}
                                aria-label="Delete Alias"
                              />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </ModalBody>

            <ModalFooter></ModalFooter>
          </ModalContent>
        </form>
      </Modal>

      <ErrorHelper toastResponse={toastResponse} />
    </Box>
  );
}
